import { useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthenticationContext';
import { usePostHogInstance } from '../analytics/AnalyticsProvider';
// Platform-agnostic location hook (works for both web and RN)
const useCurrentLocation = () => {
    // Check if we're in React Native environment
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        return { pathname: 'mobile_app' };
    }
    // Check if react-router-dom is available (web environment)
    try {
        // Only try to require if we're likely in a web environment
        if (typeof window !== 'undefined') {
            const { useLocation } = require('react-router-dom');
            return useLocation();
        }
    }
    catch {
        // Fallback
    }
    return { pathname: 'unknown' };
};
// Map URL patterns to friendly page names
const getPageName = (pathname) => {
    // Remove leading slash for consistency
    const path = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    // Handle root path
    if (path === '' || path === '/')
        return 'HomePage';
    // Handle exact matches first
    const exactMatches = {
        'login': 'LoginPage',
        'signup': 'SignupPage',
        'app': 'AppLoader',
        'dashboard': 'DashboardPage',
        'accounts': 'AccountSelectionPage',
        'updates': 'UpdateDashboardPage',
        'updates/upload': 'UploadUpdatePage',
        'updates/history': 'UpdateHistoryPage'
    };
    if (exactMatches[path]) {
        return exactMatches[path];
    }
    // Handle dynamic routes with patterns
    // Account routes: /account/123 -> AccountPage
    if (path.match(/^account\/[^\/]+$/)) {
        return 'AccountPage';
    }
    // Account sub-pages: /account/123/pairs -> AccountPairsPage
    if (path.match(/^account\/[^\/]+\/pairs$/)) {
        return 'AccountPairsPage';
    }
    if (path.match(/^account\/[^\/]+\/orders$/)) {
        return 'AccountOrdersPage';
    }
    if (path.match(/^account\/[^\/]+\/funds$/)) {
        return 'AccountFundsPage';
    }
    if (path.match(/^account\/[^\/]+\/settings$/)) {
        return 'AccountSettingsPage';
    }
    // Fallback: convert path to PascalCase + "Page"
    return path
        .split('/')
        .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join('') + 'Page';
};
export const useAnalytics = () => {
    const location = useCurrentLocation();
    const { userId, authenticated, email } = useAuth();
    const posthog = usePostHogInstance();
    // Track page views
    const trackPageView = useCallback((pageName) => {
        const friendlyPageName = pageName || getPageName(location.pathname);
        const properties = {
            page_name: friendlyPageName,
            user_authenticated: authenticated,
            user_id: userId
        };
        posthog?.capture('page_view', properties);
    }, [posthog, location.pathname, authenticated, userId]);
    // Automatically track page views when location changes
    useEffect(() => {
        trackPageView();
    }, [trackPageView]);
    // Track button/link clicks
    const trackClick = useCallback((elementName, elementType = 'button', additionalProperties) => {
        const properties = {
            element_name: elementName,
            element_type: elementType,
            page: location.pathname,
            user_authenticated: authenticated,
            ...additionalProperties
        };
        posthog?.capture('click', properties);
    }, [posthog, location.pathname, authenticated]);
    // Track login flow events
    const trackLoginFlow = useCallback((step, method, error) => {
        const properties = {
            step,
            method,
            error,
            page: location.pathname
        };
        posthog?.capture('login_flow', properties);
    }, [posthog, location.pathname]);
    // Track account page visits
    const trackAccountView = useCallback((accountId, section) => {
        const properties = {
            account_id: accountId,
            account_section: section,
            user_id: userId,
            user_email: email
        };
        posthog?.capture('account_view', properties);
    }, [posthog, userId, email]);
    // Track navigation between pages
    const trackNavigation = useCallback((fromPage, toPage, navigationMethod = 'menu') => {
        const properties = {
            from_page: fromPage,
            to_page: toPage,
            navigation_method: navigationMethod,
            user_authenticated: authenticated
        };
        posthog?.capture('navigation', properties);
    }, [posthog, authenticated]);
    // Track form interactions
    const trackFormEvent = useCallback((formName, action, error) => {
        const properties = {
            form_name: formName,
            action,
            error,
            page: location.pathname,
            user_authenticated: authenticated
        };
        posthog?.capture('form_interaction', properties);
    }, [posthog, location.pathname, authenticated]);
    // Track feature usage
    const trackFeatureUsage = useCallback((featureName, action, additionalProperties) => {
        const properties = {
            feature_name: featureName,
            action,
            page: location.pathname,
            user_id: userId,
            ...additionalProperties
        };
        posthog?.capture('feature_usage', properties);
    }, [posthog, location.pathname, userId]);
    return {
        trackPageView,
        trackClick,
        trackLoginFlow,
        trackAccountView,
        trackNavigation,
        trackFormEvent,
        trackFeatureUsage,
        // Generic capture method
        capture: (eventName, properties) => posthog?.capture(eventName, properties),
        // User identification method
        identify: (userId, properties) => posthog?.identify(userId, properties),
        // Utility to get the friendly page name for a given path
        getPageName: (path) => getPageName(path || location.pathname)
    };
};
// Export the getPageName utility function for external use
export { getPageName };
